import { Puzzle, X } from "lucide-react";
import PropTypes from "prop-types";
import { Select } from "../../../components/FormComponents";
import { TEMPLATE_CATEGORIES } from "../../../constants/themes";

export default function StepSetup({
	formData,
	setFormData,
	clients,
	companies,
	templatesByCategory,
	selectedTemplates,
	handleCategoryTemplateSelect,
	errors,
	setErrors,
	editId,
}) {
	const hasFullSelected = !!selectedTemplates.full;
	const perCatCategories = TEMPLATE_CATEGORIES.filter(
		(c) => c.value !== "full",
	);
	const companyId = formData.company_id
		? parseInt(formData.company_id, 10)
		: null;

	const filterByCompany = (list) =>
		list.filter(
			(t) => !t.company_id || !companyId || t.company_id === companyId,
		);

	return (
		<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 space-y-4">
			<h2 className="text-lg font-semibold text-white mb-2">
				Cliente e Empresa
			</h2>

			{/* Multi-template selector */}
			{!editId &&
				Object.values(templatesByCategory).some((arr) => arr.length > 0) && (
					<div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-3">
						<div className="flex items-center gap-2 mb-1">
							<Puzzle className="w-4 h-4 text-cyan-400" />
							<span className="text-sm font-semibold text-white">
								Montar com Templates
							</span>
						</div>

						{/* Full template selector */}
						{(templatesByCategory.full || []).length > 0 && (
							<div>
								<label
									htmlFor="full-template-select"
									className="block text-xs text-slate-400 mb-1"
								>
									Template Completo
								</label>
								<select
									id="full-template-select"
									value={selectedTemplates.full?.id || ""}
									onChange={(e) =>
										handleCategoryTemplateSelect("full", e.target.value)
									}
									className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
								>
									<option value="">Nenhum</option>
									{filterByCompany(templatesByCategory.full).map((t) => (
										<option key={t.id} value={t.id}>
											{t.name}
										</option>
									))}
								</select>
							</div>
						)}

						{/* Divider */}
						<div className="flex items-center gap-3">
							<div className="flex-1 h-px bg-slate-700" />
							<span className="text-[10px] text-slate-500 uppercase tracking-wider">
								ou por categoria
							</span>
							<div className="flex-1 h-px bg-slate-700" />
						</div>

						{/* Per-category selectors */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
							{perCatCategories.map((cat) => {
								const catTemplates = filterByCompany(
									templatesByCategory[cat.value] || [],
								);
								if (catTemplates.length === 0) return null;
								const CatIcon = cat.icon;
								const colorMap = {
									purple: "text-purple-400",
									emerald: "text-emerald-400",
									blue: "text-blue-400",
									amber: "text-amber-400",
								};
								return (
									<div
										key={cat.value}
										className={
											hasFullSelected ? "opacity-40 pointer-events-none" : ""
										}
									>
										<label
											htmlFor={`template-select-${cat.value}`}
											className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"
										>
											<CatIcon
												className={`w-3 h-3 ${colorMap[cat.color] || "text-slate-400"}`}
											/>
											{cat.label}
										</label>
										<div className="flex gap-1">
											<select
												id={`template-select-${cat.value}`}
												value={selectedTemplates[cat.value]?.id || ""}
												onChange={(e) =>
													handleCategoryTemplateSelect(
														cat.value,
														e.target.value,
													)
												}
												className="flex-1 bg-slate-700/50 border border-slate-600 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500"
												disabled={hasFullSelected}
											>
												<option value="">Nenhum</option>
												{catTemplates.map((t) => (
													<option key={t.id} value={t.id}>
														{t.name}
													</option>
												))}
											</select>
											{selectedTemplates[cat.value] && (
												<button
													type="button"
													onClick={() =>
														handleCategoryTemplateSelect(cat.value, "")
													}
													className="text-slate-400 hover:text-red-400 p-1"
													aria-label={`Limpar template de ${cat.label}`}
												>
													<X className="w-3 h-3" />
												</button>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

			<Select
				label="Cliente *"
				value={formData.client_id}
				onChange={(e) => {
					setFormData({ ...formData, client_id: e.target.value });
					if (errors.client_id) setErrors({ ...errors, client_id: null });
				}}
				options={clients.map((c) => ({ value: c.id, label: c.name }))}
				error={errors.client_id}
			/>
			<Select
				label="Empresa *"
				value={formData.company_id}
				onChange={(e) => {
					setFormData({ ...formData, company_id: e.target.value });
					if (errors.company_id) setErrors({ ...errors, company_id: null });
				}}
				options={companies.map((c) => ({ value: c.id, label: c.name }))}
				error={errors.company_id}
			/>
		</div>
	);
}

StepSetup.propTypes = {
	formData: PropTypes.object.isRequired,
	setFormData: PropTypes.func.isRequired,
	clients: PropTypes.array.isRequired,
	companies: PropTypes.array.isRequired,
	templatesByCategory: PropTypes.object.isRequired,
	selectedTemplates: PropTypes.object.isRequired,
	handleCategoryTemplateSelect: PropTypes.func.isRequired,
	errors: PropTypes.object.isRequired,
	setErrors: PropTypes.func.isRequired,
	editId: PropTypes.string,
};
